import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { animeList } from "@/mocks/anime";
import { newsList } from "@/mocks/news";
import { categories } from "@/mocks/categories";
import AnimeCard from "@/components/feature/AnimeCard";
import SearchBar from "@/components/feature/SearchBar";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function Search() {
  const [params] = useSearchParams();
  const q = params.get("q")?.trim() ?? "";
  const [tab, setTab] = useState<"anime" | "news">("anime");

  useDocumentTitle(
    q ? `「${q}」搜索结果 - 动漫网站` : "搜索 - 动漫网站",
    q ? `动漫网站关于「${q}」的动漫资源与资讯搜索结果。` : "搜索动漫资源与资讯。"
  );

  const keyword = q.toLowerCase();

  const animeResults = useMemo(() => {
    if (!keyword) return [];
    return animeList.filter(
      (a) =>
        a.title.toLowerCase().includes(keyword) ||
        a.category.toLowerCase().includes(keyword) ||
        a.region.toLowerCase().includes(keyword) ||
        a.tags.some((t) => t.toLowerCase().includes(keyword)) ||
        a.synopsis.toLowerCase().includes(keyword)
    );
  }, [keyword]);

  const newsResults = useMemo(() => {
    if (!keyword) return [];
    return newsList.filter(
      (n) => n.title.toLowerCase().includes(keyword) || n.summary.toLowerCase().includes(keyword)
    );
  }, [keyword]);

  const total = animeResults.length + newsResults.length;

  return (
    <div className="mx-auto max-w-[1280px] px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb items={[{ label: "搜索结果" }]} />

      <header className="mt-5 mb-6">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">搜索结果</h1>
        <p className="text-sm text-foreground-600 mt-2">
          {q ? (
            <>
              关键词「<strong className="text-primary-600">{q}</strong>」共找到{" "}
              <strong className="text-primary-600">{total}</strong> 条结果
            </>
          ) : (
            "输入关键词，搜索你想要的动漫与资讯"
          )}
        </p>
      </header>

      <div className="max-w-3xl mb-7">
        <SearchBar initialValue={q} size="md" />
      </div>

      {q ? (
        <>
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit mb-6">
            <button
              onClick={() => setTab("anime")}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                tab === "anime" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:bg-background-50"
              }`}
            >
              动漫 ({animeResults.length})
            </button>
            <button
              onClick={() => setTab("news")}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                tab === "news" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:bg-background-50"
              }`}
            >
              资讯 ({newsResults.length})
            </button>
          </div>

          {tab === "anime" &&
            (animeResults.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {animeResults.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
            ) : (
              <EmptyState keyword={q} type="动漫" onSuggest={() => setTab("news")} hasOther={newsResults.length > 0} />
            ))}

          {tab === "news" &&
            (newsResults.length > 0 ? (
              <div className="flex flex-col gap-4 max-w-4xl">
                {newsResults.map((news) => (
                  <Link
                    key={news.id}
                    to={`/news/${news.id}`}
                    className="group flex gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all"
                  >
                    <div className="w-24 sm:w-32 h-20 rounded-md overflow-hidden bg-background-200 shrink-0">
                      <img
                        src={news.cover}
                        alt={news.title}
                        title={`${news.title} - 动漫资讯`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="min-w-0">
                      <h2 className="font-heading font-semibold text-sm md:text-base text-foreground-950 group-hover:text-primary-600 transition-colors clamp-2">
                        {news.title}
                      </h2>
                      <p className="text-xs text-foreground-500 mt-2 clamp-2 leading-relaxed hidden sm:block">
                        {news.summary}
                      </p>
                      <p className="text-xs text-foreground-400 mt-2">{news.publishedAt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <EmptyState keyword={q} type="资讯" onSuggest={() => setTab("anime")} hasOther={animeResults.length > 0} />
            ))}

          <div className="mt-12">
            <h2 className="font-heading font-semibold text-foreground-950 mb-4">大家都在看</h2>
            <div className="flex flex-wrap gap-2">
              {categories.slice(0, 6).map((c) => (
                <Link
                  key={c.key}
                  to={`/search?q=${encodeURIComponent(c.name)}`}
                  className="px-3.5 py-2 rounded-md bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="py-16 text-center bg-background-100 rounded-lg border border-background-200/70">
          <i className="ri-search-eye-line text-3xl text-foreground-400" />
          <h3 className="font-heading font-semibold text-lg text-foreground-900 mt-4">开始你的搜索</h3>
          <p className="text-sm text-foreground-500 mt-2">试试输入「热血」「治愈」「科幻」等关键词</p>
        </div>
      )}
    </div>
  );
}

function EmptyState({
  keyword,
  type,
  onSuggest,
  hasOther,
}: {
  keyword: string;
  type: string;
  onSuggest: () => void;
  hasOther: boolean;
}) {
  return (
    <div className="py-16 text-center bg-background-100 rounded-lg border border-background-200/70">
      <i className="ri-emotion-sad-line text-3xl text-foreground-400" />
      <h3 className="font-heading font-semibold text-lg text-foreground-900 mt-4">
        没有找到与「{keyword}」相关的{type}
      </h3>
      <p className="text-sm text-foreground-500 mt-2">换个关键词试试，或者浏览下列推荐分类</p>
      <div className="flex items-center justify-center flex-wrap gap-3 mt-5">
        {hasOther && (
          <button
            onClick={onSuggest}
            className="h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer"
          >
            看看其他类型的结果
          </button>
        )}
        <Link
          to="/category/hot-blood"
          className="h-10 px-5 inline-flex items-center rounded-md bg-background-50 border border-background-200 text-sm text-foreground-700 hover:border-primary-300"
        >
          浏览动漫分类
        </Link>
      </div>
    </div>
  );
}